// src/App.js
// FlappyBar — Full Flappy Bird clone in React + Canvas
// Drop-in for Electron menu bar app
// All game logic, rendering, audio, and UI in one self-contained file.

import React, { useEffect, useRef, useCallback } from 'react';
import PeckerTapGame from './PeckerTapGame';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS & CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const W = 360;
const H = 520;
const GROUND_H = 64;
const GRAVITY = 0.12;   // 0.12 default
const FLAP_FORCE = -4.3;  // 4.3 default
const PIPE_WIDTH = 52;
const PIPE_CAP_H = 16;
const PIPE_SPEED_BASE = 2.2;
const PIPE_SPEED_INC = 0.042; // per point
const PIPE_GAP_BASE = 152;
const PIPE_GAP_MIN = 108;
const PIPE_GAP_DEC = 1.8;    // per point
const PIPE_SPAWN_BASE = 138;
const PIPE_SPAWN_MIN = 82;
const PIPE_SPAWN_DEC = 2;

// Colour palette
const C = {
  sky0: '#4FC3F7', sky1: '#87CEEB', sky2: '#B3E5FC',
  cloud: '#FFFFFF', cloudShadow: '#D8EEF8',
  ground: '#5D8A3C', groundStripe: '#4A7A2E', dirt: '#8B6355', dirtDot: 'rgba(0,0,0,0.12)',
  groundEdgeTop: 'rgba(255,255,255,0.22)', groundEdgeMid: 'rgba(0,0,0,0.22)',
  pipeLight: '#70D44A', pipeMid: '#5CBB3C', pipeDark: '#38801E',
  birdBody1: '#FFDE3A', birdBody2: '#FFA800', birdBelly: 'rgba(255,255,255,0.38)',
  birdWing: '#FF8C00', birdBeak: '#FF6B35', birdBeakShine: 'rgba(255,255,255,0.32)',
  birdEyeWhite: '#FFFFFF', birdEyePupil: '#1a1a2e', birdEyeShine: '#FFFFFF',
  particleA: '#FFD93D', particleB: '#FF6B35', particleC: '#FFFFFF',
  uiPanel: 'rgba(15,15,40,0.88)',
  uiBorder: '#FFD93D',
  uiGreen: '#4AA028', uiGreenBorder: '#70D44A',
  uiRed: '#FF6B6B',
  scoreText: '#FFFFFF',
  shadow: 'rgba(0,0,0,0.35)',
};

// ─────────────────────────────────────────────────────────────────────────────
// AUDIO ENGINE  (Web Audio API — no files needed)
// ─────────────────────────────────────────────────────────────────────────────
function createAudio() {
  let ac = null;

  function getCtx() {
    if (!ac) ac = new (window.AudioContext || window.webkitAudioContext)();
    if (ac.state === 'suspended') ac.resume();
    return ac;
  }

  function osc(type, freq, gain, dur, freqEnd, startTime) {
    const c = getCtx();
    const t = startTime ?? c.currentTime;
    const o = c.createOscillator();
    const g = c.createGain();
    o.connect(g);
    g.connect(c.destination);
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    if (freqEnd) o.frequency.exponentialRampToValueAtTime(freqEnd, t + dur);
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.start(t);
    o.stop(t + dur + 0.01);
  }

  return {
    flap() {
      osc('sine', 540, 0.18, 0.09, 370, null);
    },
    score() {
      const c = getCtx();
      const now = c.currentTime;
      [{ f: 660, t: 0 }, { f: 880, t: 0.07 }, { f: 1100, t: 0.14 }].forEach(({ f, t }) => {
        osc('square', f, 0.09, 0.11, null, now + t);
      });
    },
    hit() {
      osc('sawtooth', 220, 0.28, 0.18, 75, null);
      setTimeout(() => osc('sine', 110, 0.15, 0.12, 60, null), 60);
    },
    wing() {
      osc('sine', 320, 0.05, 0.04, 260, null);
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// GAME STATE FACTORY
// ─────────────────────────────────────────────────────────────────────────────
function makeGameState() {
  return {
    phase: 'menu',     // menu | ready | play | dying | dead
    frame: 0,
    score: 0,
    hiScore: parseInt(localStorage.getItem('fb_hi') || '0', 10),
    leaderboard: JSON.parse(localStorage.getItem('fb_lb') || '[]'),

    bird: makeBird(),
    pipes: [],
    clouds: makeClouds(),
    particles: [],
    floatScores: [],

    groundX: 0,
    shakeMag: 0,
    shakeX: 0,
    shakeY: 0,
    deathTimer: 0,
    menuBob: 0,
    pipeTimer: 0,
  };
}

function makeBird() {
  return {
    x: 82, y: H / 2 - 30,
    vy: 0,
    rot: 0,          // degrees
    squash: 1, stretch: 1,
    wingFrame: 0,
    blinkTimer: 90,
    blinkOpen: true,
  };
}

function makeClouds() {
  return Array.from({ length: 6 }, (_, i) => ({
    x: (W / 6) * i + Math.random() * 40,
    y: 22 + Math.random() * 110,
    w: 42 + Math.random() * 58,
    speed: 0.18 + Math.random() * 0.28,
    layer: Math.floor(Math.random() * 2),
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE — pure mutation of game state, called every rAF tick
// ─────────────────────────────────────────────────────────────────────────────
function updateState(gs, audio) {
  gs.frame++;
  gs.menuBob = Math.sin(gs.frame * 0.055) * 6;

  // Clouds scroll always
  gs.clouds.forEach(c => {
    c.x -= c.speed;
    if (c.x + c.w < 0) {
      c.x = W + 8;
      c.y = 22 + Math.random() * 110;
      c.w = 42 + Math.random() * 58;
    }
  });

  // Ground always scrolls (slower when not playing)
  const groundSpeed = (gs.phase === 'play' || gs.phase === 'dying') ? pipeSpeed(gs.score) : 0.6;
  gs.groundX = (gs.groundX - groundSpeed) % 40;

  if (gs.phase === 'menu' || gs.phase === 'ready') {
    gs.bird.y = H / 2 - 30 + gs.menuBob;
    gs.bird.rot = 0;
    gs.bird.wingFrame = wingFrame(gs.frame);
    return;
  }

  if (gs.phase === 'dying') {
    gs.deathTimer++;
    gs.bird.vy += GRAVITY * 1.6;
    gs.bird.y += gs.bird.vy;
    gs.bird.rot = Math.min(90, gs.bird.rot + 9);
    gs.shakeMag *= 0.78;
    gs.shakeX = (Math.random() - 0.5) * gs.shakeMag;
    gs.shakeY = (Math.random() - 0.5) * gs.shakeMag;
    if (gs.deathTimer > 58) gs.phase = 'dead';

    // Particles still move
    tickParticles(gs);
    return;
  }

  if (gs.phase === 'dead') return;

  // ── PLAYING ──────────────────────────────────────────────────────────────
  // Physics
  gs.bird.vy += GRAVITY;
  gs.bird.y += gs.bird.vy;

  // Rotation follows velocity
  const targetRot = Math.min(90, Math.max(-22, gs.bird.vy * 5.2));
  gs.bird.rot += (targetRot - gs.bird.rot) * 0.14;

  // Squash/stretch spring return
  gs.bird.squash  += (1 - gs.bird.squash)  * 0.22;
  gs.bird.stretch += (1 - gs.bird.stretch) * 0.22;

  // Wing animation
  gs.bird.wingFrame = wingFrame(gs.frame);

  // Blink logic
  gs.bird.blinkTimer--;
  if (gs.bird.blinkTimer <= 0) {
    gs.bird.blinkOpen = !gs.bird.blinkOpen;
    gs.bird.blinkTimer = gs.bird.blinkOpen ? 80 + Math.random() * 120 : 7;
  }

  // Shake decay
  gs.shakeMag *= 0.82;
  gs.shakeX = (Math.random() - 0.5) * gs.shakeMag;
  gs.shakeY = (Math.random() - 0.5) * gs.shakeMag;

  // Spawn pipes
  gs.pipeTimer++;
  const spawnEvery = Math.max(PIPE_SPAWN_MIN, PIPE_SPAWN_BASE - gs.score * PIPE_SPAWN_DEC);
  if (gs.pipeTimer >= spawnEvery) {
    gs.pipeTimer = 0;
    gs.pipes.push(makePipe(gs.score));
  }

  // Move pipes
  const speed = pipeSpeed(gs.score);
  gs.pipes.forEach(p => { p.x -= speed; });
  gs.pipes = gs.pipes.filter(p => p.x > -PIPE_WIDTH - 20);

  // Scoring
  gs.pipes.forEach(p => {
    if (!p.passed && p.x + PIPE_WIDTH < gs.bird.x) {
      p.passed = true;
      gs.score++;
      if (gs.score > gs.hiScore) {
        gs.hiScore = gs.score;
        localStorage.setItem('fb_hi', gs.hiScore);
      }
      audio.score();
      gs.floatScores.push({ x: gs.bird.x + 22, y: gs.bird.y - 24, val: gs.score, life: 1 });
    }
  });

  // Float scores
  gs.floatScores.forEach(f => { f.y -= 1.4; f.life -= 0.028; });
  gs.floatScores = gs.floatScores.filter(f => f.life > 0);

  // Particles
  tickParticles(gs);

  // ── COLLISION ─────────────────────────────────────────────────────────────
  const bx = gs.bird.x - 11, by = gs.bird.y - 11, bw = 22, bh = 22;

  // Ground / ceiling
  if (gs.bird.y >= H - GROUND_H - 14 || gs.bird.y <= 12) {
    triggerDeath(gs, audio);
    return;
  }

  for (const p of gs.pipes) {
    if (bx + bw > p.x + 4 && bx < p.x + PIPE_WIDTH - 4) {
      if (by < p.topH || by + bh > p.topH + p.gap) {
        triggerDeath(gs, audio);
        return;
      }
    }
  }
}

function tickParticles(gs) {
  gs.particles.forEach(p => {
    p.x  += p.vx;
    p.y  += p.vy;
    p.vy += 0.13;
    p.life -= 0.032;
  });
  gs.particles = gs.particles.filter(p => p.life > 0);
}

function triggerDeath(gs, audio) {
  if (gs.phase !== 'play') return;
  audio.hit();
  gs.phase = 'dying';
  gs.deathTimer = 0;
  gs.shakeMag = 11;
  spawnParticles(gs, gs.bird.x, gs.bird.y, 14, [C.particleA, C.particleB, C.particleC]);

  // Save leaderboard
  gs.leaderboard.push({ score: gs.score, date: Date.now() });
  gs.leaderboard.sort((a, b) => b.score - a.score);
  gs.leaderboard = gs.leaderboard.slice(0, 5);
  localStorage.setItem('fb_lb', JSON.stringify(gs.leaderboard));
}

function spawnParticles(gs, x, y, count, colors) {
  for (let i = 0; i < count; i++) {
    const angle = Math.random() * Math.PI * 2;
    const speed = 1.2 + Math.random() * 3.2;
    gs.particles.push({
      x, y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 1,
      life: 1,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: 2 + Math.random() * 4,
    });
  }
}

function makePipe(score) {
  const gap = Math.max(PIPE_GAP_MIN, PIPE_GAP_BASE - score * PIPE_GAP_DEC);
  const minY = 55, maxY = H - GROUND_H - 55 - gap;
  const topH = minY + Math.random() * (maxY - minY);
  return { x: W + 16, topH, gap, passed: false };
}

function pipeSpeed(score) {
  return PIPE_SPEED_BASE + score * PIPE_SPEED_INC;
}

function wingFrame(frame) {
  return Math.floor(frame / 6) % 3;
}

// ─────────────────────────────────────────────────────────────────────────────
// RENDERING — canvas 2D drawing functions
// ─────────────────────────────────────────────────────────────────────────────

function renderFrame(ctx, gs) {
  ctx.save();
  ctx.translate(gs.shakeX | 0, gs.shakeY | 0);

  drawSky(ctx);
  drawClouds(ctx, gs.clouds);
  drawPipes(ctx, gs.pipes);
  drawGround(ctx, gs.groundX);
  drawParticles(ctx, gs.particles);

  if (gs.phase !== 'dead') {
    drawBird(ctx, gs.bird);
  }

  drawFloatScores(ctx, gs.floatScores);
  ctx.restore();

  // UI (no shake)
  if (gs.phase === 'menu')  drawMenuUI(ctx, gs);
  if (gs.phase === 'ready') { drawScoreHUD(ctx, gs.score); drawReadyUI(ctx, gs.frame); }
  if (gs.phase === 'play')  drawScoreHUD(ctx, gs.score);
  if (gs.phase === 'dying' || gs.phase === 'dead') {
    drawScoreHUD(ctx, gs.score);
    drawDeadUI(ctx, gs);
  }
}

// ── Sky ───────────────────────────────────────────────────────────────────────
function drawSky(ctx) {
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, C.sky0);
  g.addColorStop(0.55, C.sky1);
  g.addColorStop(1, C.sky2);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);
}

// ── Clouds ────────────────────────────────────────────────────────────────────
function drawClouds(ctx, clouds) {
  clouds.forEach(c => {
    ctx.globalAlpha = c.layer === 0 ? 0.55 : 0.92;
    drawCloud(ctx, c.x, c.y, c.w);
    ctx.globalAlpha = 1;
  });
}

function drawCloud(ctx, x, y, w) {
  const h = w * 0.44;
  ctx.fillStyle = C.cloud;
  ctx.beginPath();
  [[0.18,0.70,0.42],[0.38,0.53,0.58],[0.62,0.38,0.68],[0.78,0.69,0.44],[0.50,0.76,0.50]]
    .forEach(([lx, ly, lr]) => {
      const cx = x + w * lx, cy = y + h * ly, radius = h * lr;
      ctx.moveTo(cx + radius, cy);
      ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    });
  ctx.fill();
}

// ── Pipes ─────────────────────────────────────────────────────────────────────
function drawPipes(ctx, pipes) {
  pipes.forEach(p => drawPipe(ctx, p.x, p.topH, p.gap));
}

function drawPipe(ctx, px, topH, gap) {
  const capX = px - 5;
  const capW = PIPE_WIDTH + 10;

  // Helper: pipe gradient (left-to-right shading)
  const pGrad = (y, h) => {
    const g = ctx.createLinearGradient(px, y, px + PIPE_WIDTH, y);
    g.addColorStop(0,    C.pipeDark);
    g.addColorStop(0.22, C.pipeLight);
    g.addColorStop(0.78, C.pipeMid);
    g.addColorStop(1,    C.pipeDark);
    return g;
  };
  const cGrad = (y, h) => {
    const g = ctx.createLinearGradient(capX, y, capX + capW, y);
    g.addColorStop(0,    C.pipeDark);
    g.addColorStop(0.18, C.pipeLight);
    g.addColorStop(0.82, C.pipeMid);
    g.addColorStop(1,    C.pipeDark);
    return g;
  };

  // TOP pipe shaft
  ctx.fillStyle = pGrad(0, topH);
  ctx.fillRect(px, 0, PIPE_WIDTH, topH - PIPE_CAP_H);
  // TOP cap
  ctx.fillStyle = cGrad(0, PIPE_CAP_H);
  ctx.fillRect(capX, topH - PIPE_CAP_H, capW, PIPE_CAP_H);
  // Shine strip top shaft
  ctx.fillStyle = 'rgba(255,255,255,0.22)';
  ctx.fillRect(px + 5, 0, 6, topH - PIPE_CAP_H);
  ctx.fillRect(capX + 4, topH - PIPE_CAP_H, 6, PIPE_CAP_H);
  // Dark right edge
  ctx.fillStyle = 'rgba(0,0,0,0.14)';
  ctx.fillRect(px + PIPE_WIDTH - 9, 0, 9, topH);

  // BOTTOM pipe
  const botY = topH + gap;
  const botShaft = H - botY - PIPE_CAP_H;
  ctx.fillStyle = pGrad(botY, botShaft);
  ctx.fillRect(px, botY + PIPE_CAP_H, PIPE_WIDTH, botShaft);
  // BOTTOM cap
  ctx.fillStyle = cGrad(botY, PIPE_CAP_H);
  ctx.fillRect(capX, botY, capW, PIPE_CAP_H);
  // Shine
  ctx.fillStyle = 'rgba(255,255,255,0.22)';
  ctx.fillRect(px + 5, botY + PIPE_CAP_H, 6, botShaft);
  ctx.fillRect(capX + 4, botY, 6, PIPE_CAP_H);
  // Dark right edge
  ctx.fillStyle = 'rgba(0,0,0,0.14)';
  ctx.fillRect(px + PIPE_WIDTH - 9, botY, 9, H - botY);
}

// ── Ground ────────────────────────────────────────────────────────────────────
function drawGround(ctx, gx) {
  const gY = H - GROUND_H;
  // Grass
  ctx.fillStyle = C.ground;
  ctx.fillRect(0, gY, W, GROUND_H);
  // Dirt
  ctx.fillStyle = C.dirt;
  ctx.fillRect(0, gY + 18, W, GROUND_H - 18);
  // Grass stripes
  for (let i = 0; i < W + 44; i += 22) {
    ctx.fillStyle = C.groundStripe;
    ctx.fillRect(((i + gx) % W) - 4, gY, 11, 18);
  }
  // Dirt texture
  ctx.fillStyle = C.dirtDot;
  for (let i = 0; i < W; i += 15) {
    ctx.fillRect(((i + gx * 0.48) % W) | 0, gY + 22, 7, 4);
    ctx.fillRect(((i + 7 + gx * 0.48) % W) | 0, gY + 32, 5, 3);
  }
  // Edge lines
  ctx.fillStyle = C.groundEdgeTop; ctx.fillRect(0, gY, W, 2);
  ctx.fillStyle = C.groundEdgeMid; ctx.fillRect(0, gY + 18, W, 2);
}

// ── Bird ──────────────────────────────────────────────────────────────────────
function drawBird(ctx, bird) {
  const { x, y, rot, squash, stretch, wingFrame, blinkOpen } = bird;

  ctx.save();
  ctx.translate(x | 0, y | 0);
  ctx.rotate((rot * Math.PI) / 180);
  ctx.scale(stretch, squash);

  const r = 15; // body radius

  // Drop shadow
  ctx.save();
  ctx.globalAlpha = 0.13;
  ctx.fillStyle = '#000';
  ctx.beginPath();
  ctx.ellipse(2, r + 3, r, 5, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();

  // Body outer (darker yellow)
  ctx.fillStyle = C.birdBody2;
  ctx.beginPath();
  ctx.roundRect(-r, -r, r * 2, r * 2, 8);
  ctx.fill();

  // Body inner (bright yellow)
  ctx.fillStyle = C.birdBody1;
  ctx.beginPath();
  ctx.roundRect(-r + 2, -r + 2, r * 2 - 4, r * 2 - 4, 7);
  ctx.fill();

  // Belly shine
  ctx.fillStyle = C.birdBelly;
  ctx.beginPath();
  ctx.ellipse(-3, -4, 7, 5, -0.3, 0, Math.PI * 2);
  ctx.fill();

  // Wing (3-frame flap: [resting, mid, up])
  const wingYs  = [3, 5, 7];
  const wf = Math.min(wingFrame, 2);
  ctx.fillStyle = C.birdWing;
  ctx.beginPath();
  ctx.roundRect(-r - 1, wingYs[wf], 11, 7, 3);
  ctx.fill();
  ctx.fillStyle = 'rgba(255,255,255,0.22)';
  ctx.fillRect(-r + 1, wingYs[wf] + 1, 5, 2);

  // Eye white
  ctx.fillStyle = C.birdEyeWhite;
  ctx.beginPath();
  ctx.arc(5, -4, 5.5, 0, Math.PI * 2);
  ctx.fill();

  if (blinkOpen) {
    // Pupil
    ctx.fillStyle = C.birdEyePupil;
    ctx.beginPath();
    ctx.arc(6, -4, 3.2, 0, Math.PI * 2);
    ctx.fill();
    // Shine
    ctx.fillStyle = C.birdEyeShine;
    ctx.beginPath();
    ctx.arc(7, -5.5, 1.1, 0, Math.PI * 2);
    ctx.fill();
  } else {
    // Blink: closed lid
    ctx.fillStyle = C.birdBody2;
    ctx.fillRect(1, -7, 10, 5);
    ctx.fillStyle = C.birdEyePupil;
    ctx.fillRect(2, -6, 8, 2);
  }

  // Beak
  ctx.fillStyle = C.birdBeak;
  ctx.beginPath();
  ctx.moveTo(r - 1, -3.5);
  ctx.lineTo(r + 10, 0.5);
  ctx.lineTo(r - 1, 4.5);
  ctx.closePath();
  ctx.fill();
  // Beak shine
  ctx.fillStyle = C.birdBeakShine;
  ctx.beginPath();
  ctx.moveTo(r, -2.5);
  ctx.lineTo(r + 8, 0.5);
  ctx.lineTo(r, 1);
  ctx.closePath();
  ctx.fill();

  ctx.restore();
}

// ── Particles ─────────────────────────────────────────────────────────────────
function drawParticles(ctx, particles) {
  particles.forEach(p => {
    ctx.save();
    ctx.globalAlpha = Math.max(0, p.life);
    ctx.fillStyle = p.color;
    ctx.beginPath();
    ctx.roundRect(p.x - p.size / 2, p.y - p.size / 2, p.size, p.size, 2);
    ctx.fill();
    ctx.restore();
  });
}

// ── Float scores ──────────────────────────────────────────────────────────────
function drawFloatScores(ctx, floatScores) {
  floatScores.forEach(f => {
    ctx.save();
    ctx.globalAlpha = Math.max(0, f.life);
    pixelText(ctx, `+1`, f.x, f.y, 18, C.uiBorder);
    ctx.restore();
  });
}

// ── HUD score ─────────────────────────────────────────────────────────────────
function drawScoreHUD(ctx, score) {
  const s = score.toString();
  ctx.fillStyle = 'rgba(0,0,0,0.32)';
  roundRect(ctx, W / 2 - 38, 8, 76, 40, 9);
  ctx.fill();
  pixelText(ctx, s, W / 2, 29, 26, '#FFFFFF');
}

// ── Menu UI ───────────────────────────────────────────────────────────────────
function drawMenuUI(ctx, gs) {
  // Title backdrop
  const bg = ctx.createLinearGradient(0, 90, 0, 175);
  bg.addColorStop(0, 'rgba(0,0,0,0)');
  bg.addColorStop(0.35, 'rgba(0,0,0,0.45)');
  bg.addColorStop(0.65, 'rgba(0,0,0,0.45)');
  bg.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 100, W, 90);

  pixelText(ctx, 'FLAPPY', W / 2, 132, 38, '#FFD93D');
  pixelText(ctx, 'BAR', W / 2, 170, 38, '#FF8C00');

  // Best score
  ctx.fillStyle = 'rgba(0,0,0,0.42)';
  roundRect(ctx, W / 2 - 72, 194, 144, 36, 9);
  ctx.fill();
  pixelText(ctx, `BEST  ${gs.hiScore}`, W / 2, 212, 15, '#FFD93D');

  // Tap prompt
  const pulse = 0.5 + 0.5 * Math.sin(gs.frame * 0.1);
  ctx.globalAlpha = 0.72 + pulse * 0.28;
  ctx.fillStyle = 'rgba(0,0,0,0.42)';
  roundRect(ctx, W / 2 - 102, 245, 204, 46, 11);
  ctx.fill();
  pixelText(ctx, 'TAP  TO  START', W / 2, 268, 17, '#FFFFFF');
  ctx.globalAlpha = 1;

  // Leaderboard
  if (gs.leaderboard.length > 0) {
    const rows = Math.min(gs.leaderboard.length, 4);
    const panelH = rows * 22 + 28;
    ctx.fillStyle = 'rgba(0,0,0,0.36)';
    roundRect(ctx, W / 2 - 92, 304, 184, panelH, 9);
    ctx.fill();
    pixelText(ctx, 'TOP  SCORES', W / 2, 320, 11, '#FFD93D');
    const medalColors = ['#FFD93D', '#C0C0C0', '#CD7F32', '#AAAAAA'];
    gs.leaderboard.slice(0, 4).forEach((e, i) => {
      pixelText(ctx, `#${i + 1}   ${e.score}`, W / 2, 338 + i * 22, 13, medalColors[i] || '#AAA');
    });
  }

  // Close / quit button (top-right) — only in Electron
  if (window.electronAPI) {
    ctx.fillStyle = 'rgba(255,255,255,0.15)';
    roundRect(ctx, W - 36, 6, 28, 28, 6);
    ctx.fill();
    pixelText(ctx, '✕', W - 22, 21, 13, '#FFFFFF');
  }
}

// ── Ready UI ──────────────────────────────────────────────────────────────────
function drawReadyUI(ctx, frame) {
  ctx.fillStyle = 'rgba(0,0,0,0.38)';
  roundRect(ctx, W / 2 - 82, 56, 164, 38, 11);
  ctx.fill();
  pixelText(ctx, 'GET  READY!', W / 2, 75, 17, '#FFFFFF');

  // Bouncing arrows hint
  const bob = Math.sin(frame * 0.16) * 4;
  ctx.save();
  ctx.globalAlpha = 0.55 + 0.4 * Math.sin(frame * 0.16);
  ctx.fillStyle = '#FFFFFF';
  drawArrow(ctx, W / 2, H / 2 - 70 + bob);
  drawArrow(ctx, W / 2, H / 2 - 50 + bob);
  ctx.restore();
}

function drawArrow(ctx, cx, y) {
  ctx.beginPath();
  ctx.moveTo(cx - 9, y + 7);
  ctx.lineTo(cx + 9, y + 7);
  ctx.lineTo(cx, y);
  ctx.closePath();
  ctx.fill();
}

// ── Dead / Game Over UI ───────────────────────────────────────────────────────
function drawDeadUI(ctx, gs) {
  if (gs.deathTimer < 22 && gs.phase === 'dying') return;

  // Dark overlay
  ctx.fillStyle = 'rgba(0,0,0,0.52)';
  ctx.fillRect(0, 0, W, H);

  // Panel
  const px = W / 2 - 112, py = 118, pw = 224, ph = 224;
  ctx.fillStyle = C.uiPanel;
  roundRect(ctx, px, py, pw, ph, 14);
  ctx.fill();
  ctx.strokeStyle = C.uiBorder;
  ctx.lineWidth = 2.5;
  roundRect(ctx, px, py, pw, ph, 14);
  ctx.stroke();

  pixelText(ctx, 'GAME  OVER', W / 2, 152, 20, C.uiRed);

  // Divider
  ctx.fillStyle = 'rgba(255,255,255,0.08)';
  ctx.fillRect(px + 18, 168, pw - 36, 1);

  // Score columns
  pixelText(ctx, 'SCORE', W / 2 - 54, 187, 11, '#999');
  pixelText(ctx, 'BEST', W / 2 + 54, 187, 11, '#999');
  pixelText(ctx, `${gs.score}`, W / 2 - 54, 214, 28, '#FFFFFF');

  const isNewHi = gs.score > 0 && gs.score >= gs.hiScore;
  pixelText(ctx, `${gs.hiScore}`, W / 2 + 54, 214, 28, isNewHi ? '#FFD93D' : '#FFFFFF');
  if (isNewHi) {
    const p2 = 0.65 + 0.35 * Math.sin(gs.frame * 0.22);
    ctx.save();
    ctx.globalAlpha = p2;
    pixelText(ctx, '★ NEW!', W / 2 + 54, 234, 10, '#FFD93D');
    ctx.restore();
  }

  ctx.fillStyle = 'rgba(255,255,255,0.08)';
  ctx.fillRect(px + 18, 246, pw - 36, 1);

  // Medal
  const medal = gs.score >= 40 ? '🏆' : gs.score >= 20 ? '🥇' : gs.score >= 10 ? '🥈' : gs.score >= 5 ? '🥉' : '';
  if (medal) pixelText(ctx, medal, W / 2, 272, 28, '#FFFFFF');

  // Restart button
  if (gs.phase === 'dead') {
    const pulse = 0.82 + 0.18 * Math.sin(gs.frame * 0.13);
    ctx.save();
    ctx.globalAlpha = pulse;
    ctx.fillStyle = C.uiGreen;
    roundRect(ctx, W / 2 - 78, 312, 156, 44, 11);
    ctx.fill();
    ctx.strokeStyle = C.uiGreenBorder;
    ctx.lineWidth = 2;
    roundRect(ctx, W / 2 - 78, 312, 156, 44, 11);
    ctx.stroke();
    ctx.restore();
    pixelText(ctx, 'PLAY  AGAIN', W / 2, 334, 17, '#FFFFFF');
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// UTILS
// ─────────────────────────────────────────────────────────────────────────────
function pixelText(ctx, text, x, y, size, color, align = 'center') {
  ctx.save();
  ctx.font = `900 ${size}px 'Courier New', Courier, monospace`;
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';
  ctx.strokeStyle = 'rgba(0,0,0,0.72)';
  ctx.lineWidth = size * 0.19;
  ctx.lineJoin = 'round';
  ctx.strokeText(text, x, y);
  ctx.fillStyle = color;
  ctx.fillText(text, x, y);
  ctx.restore();
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  if (ctx.roundRect) {
    ctx.roundRect(x, y, w, h, r);
  } else {
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// REACT COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
// Kept as a named export so the in-progress legacy canvas work remains available
// while Electron and the browser use the shared, feature-complete engine below.
export function LegacyApp() {
  const canvasRef = useRef(null);
  const gsRef     = useRef(null);   // mutable game state (no re-renders needed)
  const audioRef  = useRef(null);
  const rafRef    = useRef(null);

  // ── Init ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    audioRef.current = createAudio();
    gsRef.current = makeGameState();

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    // Scale for HiDPI / Retina
    const dpr = window.devicePixelRatio || 1;
    canvas.width  = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width  = W + 'px';
    canvas.style.height = H + 'px';
    ctx.scale(dpr, dpr);

    // Main loop
    function loop() {
      updateState(gsRef.current, audioRef.current);
      renderFrame(ctx, gsRef.current);
      rafRef.current = requestAnimationFrame(loop);
    }
    rafRef.current = requestAnimationFrame(loop);

    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // ── Input handler ──────────────────────────────────────────────────────────
  const handleInput = useCallback((clientX, clientY) => {
    const gs    = gsRef.current;
    const audio = audioRef.current;
    const canvas = canvasRef.current;
    if (!gs || !canvas) return;

    // Resume AudioContext (browser policy)
    audio.flap && audio.flap(); // warm up — but we only want the sound on flap action

    const rect = canvas.getBoundingClientRect();
    const scaleX = W / rect.width;
    const cx = (clientX - rect.left) * scaleX;
    const cy = (clientY - rect.top)  * (H / rect.height);

    // Menu close button (Electron only)
    if (window.electronAPI && cx > W - 38 && cx < W - 8 && cy > 4 && cy < 34) {
      window.electronAPI.closeWindow();
      return;
    }

    if (gs.phase === 'dead') {
      // Check restart button hit area
      if (cx > W / 2 - 78 && cx < W / 2 + 78 && cy > 312 && cy < 356) {
        gsRef.current = makeGameState();
        gsRef.current.phase = 'ready';
        gsRef.current.hiScore = parseInt(localStorage.getItem('fb_hi') || '0', 10);
        gsRef.current.leaderboard = JSON.parse(localStorage.getItem('fb_lb') || '[]');
        gsRef.current.clouds = makeClouds();
      }
      return;
    }

    if (gs.phase === 'menu') {
      gs.phase = 'ready';
      return;
    }
    if (gs.phase === 'ready') {
      gs.phase = 'play';
    }
    if (gs.phase !== 'play') return;

    // FLAP
    gs.bird.vy      = FLAP_FORCE;
    gs.bird.squash  = 0.68;
    gs.bird.stretch = 1.32;
    audio.flap();
  }, []);

  // ── Keyboard ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (['Space', 'ArrowUp', 'KeyW'].includes(e.code)) {
        e.preventDefault();
        const gs = gsRef.current;
        if (!gs) return;
        if (gs.phase === 'dead') {
          gsRef.current = makeGameState();
          gsRef.current.phase = 'ready';
          gsRef.current.hiScore = parseInt(localStorage.getItem('fb_hi') || '0', 10);
          gsRef.current.leaderboard = JSON.parse(localStorage.getItem('fb_lb') || '[]');
          gsRef.current.clouds = makeClouds();
          return;
        }
        // Simulate a center-canvas click for flap
        const canvas = canvasRef.current;
        if (canvas) {
          const rect = canvas.getBoundingClientRect();
          handleInput(rect.left + rect.width / 2, rect.top + rect.height / 2);
        }
      }
      // Escape closes window in Electron
      if (e.code === 'Escape' && window.electronAPI) {
        window.electronAPI.closeWindow();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleInput]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#87CEEB',
      }}
    >
      <div
        style={{
          width: W,
          height: H,
          overflow: 'hidden',
          borderRadius: 12,
          boxShadow: '0 8px 40px rgba(0,0,0,0.5)',
          cursor: 'pointer',
          userSelect: 'none',
          WebkitUserSelect: 'none',
          position: 'relative',
        }}
      >
      <canvas
          ref={canvasRef}
          style={{ display: 'block' }}
          onClick={(e) => handleInput(e.clientX, e.clientY)}
          onTouchStart={(e) => {
            e.preventDefault();
            handleInput(e.touches[0].clientX, e.touches[0].clientY);
          }}
        />
      </div>
    </div>
  );
}

export default function App() {
  return (
    <div style={{
      width: '100vw', height: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', overflow: 'hidden', background: '#0d0f1a',
    }}>
      <PeckerTapGame width={360} height={540} autoMusic borderRadius={window.electronAPI ? 0 : 12} />
    </div>
  );
}
